import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { useQuery } from "@tanstack/react-query";
import { client } from "../lib/api";
import { ReactNode, useEffect, useState } from "react";

interface ProtectedRouteProps {
  children: ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuthStore();
  const location = useLocation();
  const [timeoutReached, setTimeoutReached] = useState(false);

  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ["user-profile"],
    queryFn: () => client.users.getUsersProfile(),
    enabled: !!user,
    retry: false,
  });

  // Safety timeout: if loading takes more than 5 seconds, assume failure
  useEffect(() => {
    if (loading) {
      console.log("ProtectedRoute: Waiting for session check...");
      const timeout = setTimeout(() => {
        console.log(
          "ProtectedRoute: Timeout reached, assuming not authenticated",
        );
        setTimeoutReached(true);
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [loading]);

  if (loading && !timeoutReached) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-secondary">
        <div className="text-text-primary">Loading...</div>
      </div>
    );
  }

  if (!user || timeoutReached) {
    // Save the current path for redirect after login
    const currentPath = location.pathname + location.search;
    if (currentPath !== "/login") {
      sessionStorage.setItem("redirectAfterLogin", currentPath);
      console.log("ProtectedRoute: Saving path for redirect:", currentPath);
    }
    return <Navigate to="/login" replace />;
  }

  // Wait for profile to load before checking terms
  if (profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-secondary">
        <div className="text-text-primary">Loading...</div>
      </div>
    );
  }

  // Redirect to terms if not yet accepted
  if (profileData?.user && !(profileData.user as any).termsAcceptedAt) {
    return <Navigate to="/terms" replace />;
  }

  return <>{children}</>;
}
